import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Text } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'

import { useBrandContext } from '@/context/brand-context'
import { useGenerateDesigns } from '@/api/generate-designs'
import { useUploadGraphics } from '@/hooks/use-upload-graphics'
import GenerationAnimation from '@/views/gallery/components/generation-animation'
import StepProgress from './components/step-progress'
import BrandAnalysisStep from './components/brand-analysis-step'
import SentimentStep from './components/sentiment-step'
import UploadGraphicsStep from './components/upload-graphics-step'

const TOTAL_STEPS = 3

const stepTransition = {
  initial: { opacity: 0, y: 24, filter: 'blur(4px)' },
  animate: { opacity: 1, y: 0, filter: 'blur(0px)' },
  exit: { opacity: 0, y: -16, filter: 'blur(4px)' },
  transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] as const }
}

function OnboardingPage() {
  const [currentStep, setCurrentStep] = useState(0)
  const [skippedAnalysis, setSkippedAnalysis] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState(false)
  const navigate = useNavigate()

  const { brandAnalysis, selectedGraphics, designPrompt, creativityLevel, selectedRetailEvent } =
    useBrandContext()
  const { mutateAsync: generateDesigns } = useGenerateDesigns()
  const uploadGraphics = useUploadGraphics()

  const brandName = brandAnalysis?.brand.name ?? 'Brand'

  const goToNext = useCallback(() => {
    setCurrentStep((prev) => prev + 1)
  }, [])

  const goToPrevious = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 0))
  }, [])

  const goToStep = useCallback((step: number) => {
    setCurrentStep(step)
  }, [])

  const handleSkip = useCallback(() => {
    setSkippedAnalysis(true)
    setCurrentStep(2)
  }, [])

  const runGeneration = useCallback(async () => {
    setGenerating(true)
    setGenerateError(false)

    try {
      const subjectImageUrls = await uploadGraphics(selectedGraphics)

      await generateDesigns({
        prompt: designPrompt.trim(),
        subjectImageUrls,
        creativityLevel,
        ...(selectedRetailEvent && { trends: [selectedRetailEvent] }),
        ...(brandAnalysis && {
          brandAnalysis: {
            brand: {
              colors: brandAnalysis.brand.colors,
              visualStyle: brandAnalysis.brand.visualStyle,
              designDirective: brandAnalysis.brand.designDirective
            },
            recommendedStyles: brandAnalysis.recommendedStyles
          }
        })
      })

      navigate('/gallery')
    } catch {
      setGenerateError(true)
    }
  }, [
    brandAnalysis,
    creativityLevel,
    designPrompt,
    selectedGraphics,
    selectedRetailEvent,
    uploadGraphics,
    generateDesigns,
    navigate
  ])

  const handleGenerate = useCallback(() => {
    runGeneration()
  }, [runGeneration])

  if (generating) {
    if (generateError) {
      return (
        <Box
          position="relative"
          h="100vh"
          bg="bg.primary"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          px="24px"
        >
          <Box textAlign="center">
            <Text color="text.secondary" textStyle="body" mb="24px">
              Something went wrong generating designs. Let&apos;s try again.
            </Text>
            <Button
              h="48px"
              px="32px"
              textStyle="body"
              fontWeight={500}
              borderRadius="10px"
              bg="accent.500"
              color="white"
              _hover={{
                bg: 'accent.400',
                transform: 'translateY(-1px)',
                boxShadow: '0 0 30px rgba(0, 112, 243, 0.3)'
              }}
              transition="all 0.3s ease"
              onClick={handleGenerate}
            >
              Retry
            </Button>
          </Box>
        </Box>
      )
    }

    return (
      <Box h="100vh" bg="bg.primary">
        <GenerationAnimation brandName={brandName} />
      </Box>
    )
  }

  return (
    <Box
      position="relative"
      h="100vh"
      bg="bg.primary"
      overflow="hidden"
    >
      <StepProgress currentStep={currentStep} totalSteps={TOTAL_STEPS} stepsClickable={!!brandAnalysis} onStepClick={goToStep} />

      <Box
        position="relative"
        zIndex={1}
        display="flex"
        alignItems="flex-start"
        justifyContent="center"
        h="100vh"
        pt={{ base: '126px', md: '138px' }}
        pb={{ base: '104px', md: '112px' }}
        overflowY={{ base: 'auto', md: currentStep === 2 ? 'hidden' : 'auto' }}
      >
        <AnimatePresence mode="wait">
          {currentStep === 0 && (
            <motion.div
              key="step-0"
              {...stepTransition}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <BrandAnalysisStep onContinue={goToNext} onSkip={handleSkip} />
            </motion.div>
          )}
          {currentStep === 1 && (
            <motion.div
              key="step-1"
              {...stepTransition}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <SentimentStep onContinue={goToNext} onBack={goToPrevious} />
            </motion.div>
          )}
          {currentStep === 2 && (
            <motion.div
              key="step-2"
              {...stepTransition}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center'
              }}
            >
              <UploadGraphicsStep onBack={skippedAnalysis ? undefined : goToPrevious} onGenerate={handleGenerate} />
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  )
}

export default OnboardingPage
